import { ProductCard } from '@oneaudi/unified-web-alpha-components';
import { htmlToText } from 'html-to-text';
import { useCallback, useMemo } from 'react';
import { useDialogs } from '~/app/hooks/use-dialogs';
import { useI18n } from '~/app/i18n/i18n';
import { useMarketConfig } from '~/app/market-config';
import DifferencesAndLocation from '~/app/pages/configurator/components/dialogs/car-matching-results-dialog/components/differences-location';
import DisclaimerDialog from '~/app/pages/configurator/components/dialogs/car-matching-results-dialog/components/disclaimer-dialog';
import PriceDetail from '~/app/pages/configurator/components/dialogs/car-matching-results-dialog/components/price-detail';
import { mapTrackingData } from '~/app/pages/configurator/components/dialogs/car-matching-results-dialog/models/map-tracking-data';
import { appTrackingService } from '~/app/tracking/app-tracking-service';
import { EventAction } from '~/app/tracking/feld-m-types';
import placeholder from '~/assets/icons/placeholder.svg';
import {
	PriceFragment,
	SimilarCarCompareCriterion,
	StockCarNewFragment,
	StockCarUsedFragment,
} from '~/graphql/__generated__/graphql';
import { modifyImageResolution } from '~/graphql/utils/modify-image-url';
import { StyledCarContainer } from '../styles/styled-result-tile';

export default function ResultTile({
	similarCar,
	carIndex,
}: {
	similarCar: StockCarNewFragment | StockCarUsedFragment;
	carIndex: number;
}) {
	const { stockCar, score } = similarCar;
	const { i18n } = useI18n();
	const { config } = useMarketConfig();
	const { openDialog, isDialogOpen, closeDialog } = useDialogs();

	const formattedScore = useMemo(() => (score ? `${Math.floor(score * 100)}%` : ''), [score]);
	const hasDisclaimer = config.stockCarConfig.disclaimer;

	const { url } = !!stockCar?.images && stockCar?.images[0] ? stockCar.images[0] : { url: placeholder };

	const priceFragment: PriceFragment | undefined =
		stockCar && stockCar.carPrices && stockCar.carPrices.length > 0
			? stockCar.carPrices.find((d) => d.type === 'sale')?.price
			: undefined;

	const missingFeatures = similarCar.compareCriteria?.filter((_, index) => index < 4);
	const externalUrl = stockCar ? config.stockCarConfig.url.replace('{:vehicleId}', stockCar.id) : '';

	const isAvailableNow: boolean = stockCar.salesInfo?.availableFromDateInfo
		? stockCar.salesInfo?.availableFromDateInfo.type === 'now'
		: false;

	const trackClickEvent = useCallback(
		(eventAction: EventAction, eventName: string, label: string, elementName: string, targetURL?: string) => {
			appTrackingService.stockCarClickEvent({
				eventAction,
				eventName,
				label,
				targetURL,
				elementName,
				value: formattedScore,
				pos: `${carIndex + 1}`,
				relatedProducts: mapTrackingData(similarCar, priceFragment?.value),
			});
		},
		[],
	);

	return (
		<>
			<StyledCarContainer>
				<ProductCard
					headline={{
						text: stockCar?.model?.name || '',
					}}
					iconButton={
						hasDisclaimer
							? {
									icon: 'info-circle',
									'data-testid': 'data-testid-iconButton',
									onClick: () => {
										openDialog('foundMatchDisclaimer');
									},
									size: 'sm',
									variant: 'tertiary',
								}
							: undefined
					}
					image={{
						alt: stockCar?.model?.name ? htmlToText(stockCar?.model?.name) : 'Stock Car placeholder image',
						fallbackSrc: 'assets/images/product_tile.png',
						objectFit: 'contain',
						sizes: '(max-width: 600px) 100vw, 50vw',
						sources: [
							{
								src: modifyImageResolution(url, '444'),
								width: 400,
							},
						],
					}}
					price1={
						<PriceDetail priceFragment={priceFragment} showDisclaimer={config.stockCarConfig.disclaimer} />
					}
					slot1={
						<DifferencesAndLocation
							isAvailableNow={isAvailableNow}
							missingFeatures={missingFeatures as SimilarCarCompareCriterion[]}
							dealer={stockCar?.dealer}
						/>
					}
					tags={{
						corner: {
							label: formattedScore + ' ' + i18n('ngc.stockcar.matches.match'),
							variant: 'read-only',
							size: 'sm',
						},
					}}
					textButtons={[
						{
							children: i18n('ngc.stockcar.matches.details'),
							href: externalUrl,
							target: '_blank',
							size: 'sm',
							onClick: () => {
								trackClickEvent(
									EventAction.ExitLink,
									'configurator (stock car matching) - click on vtp link',
									i18n('ngc.stockcar.matches.details'),
									'button',
									externalUrl,
								);
							},
							variant: 'primary',
						},
					]}
				/>
			</StyledCarContainer>

			<DisclaimerDialog
				open={isDialogOpen('foundMatchDisclaimer')}
				onClose={() => closeDialog('foundMatchDisclaimer')}
			/>
		</>
	);
}
